<template>
  <div>
    <v-card tile flat>
      <v-row no-gutters>
        <v-col cols="12" md="4" lg="3">
          <v-tabs v-model="tab" :vertical="$vuetify.breakpoint.mdAndUp">
            <v-tabs-slider color="secondary"></v-tabs-slider>

            <v-tab v-for="item in actions" :key="item.name" v-model="tab">
              {{ item.title }}
            </v-tab>
          </v-tabs>
        </v-col>
        <v-col>
          <v-tabs-items v-model="tab">
            <v-tab-item v-for="item in actions" :key="item.name">
              <v-card flat class="border-left">
                <component
                  :loading="loading"
                  v-if="actionProps.find((x) => x.name == item.name)"
                  v-bind="actionProps.find((x) => x.name == item.name).props"
                  :is="item.component"
                  @setUpTask="setUpTask"
                  :disabled="disabled"
                >
                </component>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-col>
      </v-row>
    </v-card>
    <v-dialog v-model="dialog" persistent v-if="task != null">
      <v-card :loading="running">
        <template slot="progress">
          <v-progress-linear
            color="secondary"
            height="6"
            :indeterminate="task.chunks.length == 1"
            :value="percentageComplete"
          ></v-progress-linear>
        </template>
        <v-card-title
          ><span v-if="task.chunks.length > 1">Batch &nbsp; </span
          ><span v-else>Begin &nbsp; </span>
          <span class="pl-2">{{ task.action }} {{ task.name | pluralTerm }}</span>
        </v-card-title>
        <v-card-text v-if="task.chunks.length > 1 && chunkSize > 1"
          >Because you have selected more than {{ chunkSize }}
          {{ task.name | pluralTerm }}, they will be processed in batches.
        </v-card-text>
        <v-expansion-panels accordion v-if="task.chunks.length > 1 && chunkSize > 1">
          <v-expansion-panel
            v-for="(chunk, ci) in task.chunks"
            :key="ci"
            :loading="chunk.inProgress"
          >
            <v-expansion-panel-header v-slot="{ open }">
              <div class="d-flex">
                <div>
                  <h4>
                    Batch {{ ci + 1 }}: {{ chunk.items.length }}
                    {{ task.name | pluralTerm }}
                  </h4>
                  <h5 v-if="chunk.completed">
                    {{ Object.keys(chunk.success).length }} Succeeded,
                    {{ Object.keys(chunk.failure).length }} Failed
                  </h5>
                </div>

                <v-spacer></v-spacer>
                <div>
                  <v-btn
                    icon
                    color="error"
                    v-if="!chunk.completed && !chunk.inProgress"
                    @click="killChunk(ci)"
                    ><v-icon>mdi-close</v-icon></v-btn
                  >
                  <v-progress-circular
                    color="primary"
                    v-if="chunk.inProgress && !open"
                    indeterminate
                  ></v-progress-circular>

                  <v-icon
                    color="success"
                    v-show="chunk.completed && !Object.keys(chunk.failure).length"
                    >mdi-check</v-icon
                  >

                  <v-icon
                    color="warning"
                    v-show="
                      chunk.completed &&
                      Object.keys(chunk.success).length &&
                      Object.keys(chunk.failure).length
                    "
                    >mdi-alert-circle-outline</v-icon
                  >
                  <v-icon
                    color="error"
                    v-show="
                      chunk.completed &&
                      !Object.keys(chunk.success).length &&
                      Object.keys(chunk.failure).length
                    "
                    >mdi-alert-circle-outline</v-icon
                  >
                </div>
              </div>
              <v-progress-linear
                style="position: absolute; bottom: 0px; left: 0px"
                height="5"
                indeterminate
                color="primary"
                v-if="chunk.inProgress && open"
              ></v-progress-linear>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="">
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th></th>
                      <th v-if="task.request.replace">
                        <span v-if="task.action == 'duplicate'">Duplicate Name</span
                        ><span v-else>Replaced</span>
                      </th>
                      <th
                        v-if="
                          task.chunks &&
                          task.chunks[0] &&
                          task.chunks[0].items &&
                          task.chunks[0].items[0] &&
                          task.chunks[0].items[0].groupId
                        "
                      ></th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, i) in chunk.items" :key="i">
                      <th>{{ item[task.titleProp] }}</th>
                      <th v-if="task.request.replace">
                        {{
                          item[task.titleProp].replace(
                            task.request.find,
                            task.request.replace
                          )
                        }}
                      </th>
                      <td v-if="item.groupId">
                        <sr-group-chip :id="item.groupId" small></sr-group-chip>
                      </td>
                      <td v-if="task.subTitleProp">{{ item[task.subTitleProp] }}</td>
                      <td>
                        <v-tooltip left>
                          <template v-slot:activator="{ on, attrs }">
                            <div v-bind="attrs" v-on="on">
                              <v-icon color="success" v-show="item.id in chunk.success"
                                >mdi-check</v-icon
                              >
                              <v-icon color="error" v-show="item.id in chunk.failure"
                                >mdi-close</v-icon
                              >
                            </div>
                          </template>
                          <span v-if="item.id in chunk.failure"
                            >Unable to {{ task.action }} this {{ task.name | term }}</span
                          >
                          <span v-else>{{ task.action }} complete</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-card v-if="task.chunks.length == 1" flat>
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Existing</th>
                  <th v-if="task.request.replace">
                    <span v-if="task.action == 'duplicate'">Duplicate Name</span
                    ><span v-else>Replaced</span>
                  </th>
                  <th
                    v-if="
                      task.chunks &&
                      task.chunks[0] &&
                      task.chunks[0].items &&
                      task.chunks[0].items[0] &&
                      task.chunks[0].items[0].groupId
                    "
                  ></th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in task.chunks[0].items" :key="i">
                  <th>{{ item[task.titleProp] }}</th>
                  <th v-if="task.request.replace">
                    {{
                      item[task.titleProp].replace(
                        task.request.find,
                        task.request.replace
                      )
                    }}
                  </th>
                  <td v-if="item.groupId">
                    <sr-group-chip :id="item.groupId" small></sr-group-chip>
                  </td>
                  <td v-if="task.subTitleProp">{{ item[task.subTitleProp] }}</td>
                  <td>
                    <v-tooltip left>
                      <template v-slot:activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="on">
                          <v-icon
                            color="success"
                            v-show="item.id in task.chunks[0].success"
                            >mdi-check</v-icon
                          >
                          <v-icon color="error" v-show="item.id in task.chunks[0].failure"
                            >mdi-close</v-icon
                          >
                        </div>
                      </template>
                      <span v-if="item.id in task.chunks[0].failure"
                        >Unable to {{ task.action }} this {{ task.name | term }}</span
                      >
                      <span v-else>{{ task.action }} complete</span>
                    </v-tooltip>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>
        <v-card v-if="task.chunks.length > 1 && chunkSize == 1" flat>
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Existing</th>
                  <th v-if="task.request.replace">
                    <span v-if="task.action == 'duplicate'">Duplicate Name</span
                    ><span v-else>Replaced</span>
                  </th>
                  <th
                    v-if="
                      task.chunks &&
                      task.chunks[0] &&
                      task.chunks[0].items &&
                      task.chunks[0].items[0] &&
                      task.chunks[0].items[0].groupId
                    "
                  ></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(chunk, i) in task.chunks" :key="i">
                  <th>{{ chunk.items[0][task.titleProp] }}</th>
                  <th v-if="task.request.replace">
                    {{
                      chunk.items[0][task.titleProp].replace(
                        task.request.find,
                        task.request.replace
                      )
                    }}
                  </th>
                  <td v-if="chunk.items[0].groupId">
                    <sr-group-chip :id="chunk.items[0].groupId" small></sr-group-chip>
                  </td>
                  <td v-if="task.subTitleProp">
                    {{ chunk.items[0][task.subTitleProp] }}
                  </td>
                  <td>
                    <div>
                      <v-btn
                        icon
                        color="error"
                        v-if="!chunk.completed && !chunk.inProgress"
                        @click="killChunk(ci)"
                        ><v-icon>mdi-close</v-icon></v-btn
                      >
                      <v-progress-circular
                        color="primary"
                        v-if="chunk.inProgress && !open"
                        indeterminate
                      ></v-progress-circular>
                    </div>
                    <v-tooltip left>
                      <template v-slot:activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="on">
                          <v-icon
                            color="success"
                            v-show="chunk.items[0].id in chunk.success"
                            >mdi-check</v-icon
                          >
                          <v-icon
                            color="error"
                            v-show="chunk.items[0].id in chunk.failure"
                            >mdi-alert-circle-outline</v-icon
                          >
                        </div>
                      </template>
                      <span v-if="chunk.items[0].id in chunk.failure"
                        >Unable to {{ task.action }} this {{ task.name | term }}</span
                      >
                      <span v-else>{{ task.action }} complete</span>
                    </v-tooltip>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>
        <v-divider></v-divider>

        <v-card-text v-if="killed && inProgress >= 0 && !error">
          <v-alert class="ma-2" type="info" dense text
            >Process will be paused after the current
            <span v-if="chunkSize == 1">{{ "assessment" | term }}</span>
            <span v-else>batch</span> completes.
          </v-alert>
        </v-card-text>
        <v-alert tile prominent v-if="error != null" type="error">
          <v-row align="center">
            <v-col class="grow"> No {{ task.action }} completed: {{ error }} </v-col>
            <v-col class="shrink">
              <v-btn text dark @click="reset">Cancel</v-btn>
            </v-col>
          </v-row>
        </v-alert>
        <v-card-actions v-else>
          <v-btn
            v-if="killed || !incomplete"
            :disabled="killed && inProgress >= 0"
            color="info"
            text
            @click="downloadCSV"
          >
            <v-icon left>mdi-download</v-icon>
            results</v-btn
          >

          <v-spacer></v-spacer>
          <v-btn
            v-if="killed"
            :disabled="killed && inProgress >= 0"
            text
            color="error"
            @click="reset"
          >
            Cancel Remaining Batches</v-btn
          >
          <v-btn v-if="inProgress == -1 && incomplete" color="primary" @click="begin">
            <span v-if="!killed"> Begin</span> <span v-else>Resume</span>
          </v-btn>
          <v-btn
            v-if="inProgress == -1 && incomplete && !killed"
            text
            color="error"
            @click="reset"
          >
            Cancel
          </v-btn>
          <v-btn v-if="inProgress >= 0 && !killed" color="error" @click="killAction"
            ><v-icon left>mdi-pause-circle</v-icon>Pause</v-btn
          >
          <v-btn v-if="!incomplete" color="primary" @click="reset"> Done</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "MultipleActions",
  data() {
    return {
      tab: null,
      components: [],
      loading: false,
      actionProps: [],
      task: null,
      dialog: false,
      chunkSize: 1,
      chunkIndex: -1,
      running: false,
      killed: false,
      csv: null,
      emit: null,
      error: null,
    };
  },
  watch: {
    count: function () {
      console.log("count changed");
      this.init();
    },
    chunkIndex: function (val) {
      if (this.running) {
        console.log(`Running Chunk ${val}`);
        this.run();
      } else {
        console.log("Stopping");
        if (this.emit != null) {
          console.log(`emitting action: ${this.emit}`);
          this.$emit(this.emit);
        }
      }
    },
  },
  mounted() {
    console.log("mounted");
    this.init();
  },
  computed: {
    percentageComplete() {
      if (this.task && this.task.chunks && this.task.chunks.length) {
        var complete = this.task.chunks.filter((c) => c.completed).length;
        var all = this.task.chunks.length ? this.task.chunks.length : 1;
        return (complete / all) * 100.0;
      }
      return 0;
    },
    incomplete() {
      if (this.task) {
        var chunk = this.task.chunks.find((c) => !c.completed);
        if (chunk) {
          return true;
        }
      }
      return false;
    },
    inProgress() {
      if (this.task) {
        var chunk = this.task.chunks.find((c) => c.inProgress);
        return this.task.chunks.indexOf(chunk);
      }
      return null;
    },
    count() {
      return this.items.length;
    },
  },
  methods: {
    downloadCSV() {
      var data = [];
      const hs = this.csv.headers;
      this.task.chunks.forEach((chunk, index) => {
        if (chunk.completed) {
          var successes = Object.keys(chunk.success);
          var failures = Object.keys(chunk.failure);
          console.log(`chunk ${index}`);
          console.log(successes);
          console.log(failures);
          successes.forEach((s) => {
            var result = "-";
            var item = this.items.find((i) => i.id == s);
            var datum = hs.map((h) => {
              if (h.isItemProp) {
                result = h.value(item);
              } else if (h.isValueProp && !h.requireFailure) {
                result = h.value(chunk.success[s]);
              }
              return result;
            });
            datum.push("Success");
            datum.push(index + 1);
            console.log(datum);
            data.push(datum);
          });
          failures.forEach((f) => {
            var item = this.items.find((i) => i.id == f);
            var datum = hs.map((h) => {
              var result = "-";
              if (h.isItemProp) {
                result = h.value(item);
              } else if (h.isValueProp && !h.requireSuccess) {
                result = h.value(chunk.success[f]);
              }
              return result;
            });
            datum.push("Failed");
            datum.push(index + 1);
            console.log(datum);
            data.push(datum);
          });
        } else {
          chunk.items.forEach((item) => {
            console.log(`chunk incomplete: ${index}`);
            var datum = hs.map((h) => {
              var result = "-";
              if (h.isItemProp) {
                result = h.value(item);
              }
              return result;
            });
            datum.push("Canceled- Not started");
            datum.push(index + 1);
            data.push(datum);
          });
        }
      });
      var headers = this.csv.headers.map((h) => h.key);
      headers.push("Status");
      headers.push("Batch Number");
      var obj = {
        headers: headers,
        data: data,
        title: this.csv.title,
      };
      console.log(obj);
      this.exportCSVFile(headers, data, this.csv.title);
    },
    reset() {
      if (this.task.clearSelection) {
        this.$emit("clearSelection");
      }
      this.task = null;
      this.chunkIndex = -1;
      this.running = false;
      this.killed = false;
      this.csv = null;
      this.dialog = false;
      this.error = null;
      this.emit = null;
    },
    killAction() {
      this.killed = true;
      this.running = false;
    },
    killChunk(index) {
      this.task.chunks.splice(index, 1);
    },
    doActionTimeout(chunk, data) {
      const ids = chunk.items.map((c) => c.id);
      const index = this.chunkIndex;
      var request = data.request;
      request[data.dataProp] = ids;
      chunk.inProgress = true;
      var that = this;

      setTimeout(function () {
        console.log(request);
        //fake results
        let success = new Object();
        let failure = new Object();
        if (index != 1) {
          ids.forEach((item, i) => {
            success[item] = `Copy of ${item} (${i})`;
          });
        } else {
          ids.forEach((item, i) => {
            if (i == 1) {
              failure[item] = `Unable to copy ${item} (${i})`;
            } else {
              success[item] = `Copy of ${item} (${i})`;
            }
          });
        }
        that.task.chunks[index].inProgress = false;
        that.task.chunks[index].success = success;
        that.task.chunks[index].failure = failure;
        that.task.chunks[index].completed = true;
        console.log("setting chunk index");
        that.chunkIndex = index + 1;
        console.log(that.chunkIndex);
        //return true;
      }, 5000);
    },
    doAction(chunk, data) {
      var ids = chunk.items; //.map((c) => c.id);
      if (!data.includeSecret) {
        ids = ids.map((c) => c.id);
      } else {
        ids = ids.map((c) => {
          return {
            id: c.id,
            secret: c.secret,
          };
        });
      }
      const index = this.chunkIndex;
      var request = data.request;
      request[data.dataProp] = ids;
      chunk.inProgress = true;
      var that = this;
      /* var data = {
        assessments: this.availableIds,
        tags: this.tags,
        find: this.find,
        replace: this.replace,
        copyMarks: this.copyMarks,
        locked: this.locked,
        archived: false,
      };
      */
      console.log(request);
      this.$post(data.url, request).then((response) => {
        debugger;
        if (response.error) {
          this.error = response.text;
          this.killAction();
        } else {
          that.task.chunks[index].inProgress = false;
          that.task.chunks[index].success = response.success;
          that.task.chunks[index].failure = response.failure;
          that.task.chunks[index].completed = true;
          console.log("setting chunk index");
          that.chunkIndex = index + 1;
          console.log(that.chunkIndex);
        }
        // this.loading = false;
      });
    },
    run() {
      if (this.chunkIndex < this.task.chunks.length) {
        console.log(`Running: Chunk Index: ${this.chunkIndex}`);
        debugger;
        const chunk = this.task.chunks[this.chunkIndex];
        const request = this.task;
        this.doAction(chunk, request);
      } else {
        this.running = false;
        console.log(`Stop Running: Chunk Index: ${this.chunkIndex}`);
        this.chunkIndex = -1;
      }
    },
    begin() {
      console.log("Beginning");
      this.running = true;
      if (this.killed) {
        this.killed = false;
        this.run();
      } else {
        this.chunkIndex = 0;
      }
    },
    chunk(arr) {
      const chunkSize = this.chunkSize;
      var result = [];
      for (let i = 0; i < arr.length; i += chunkSize) {
        const chunk = arr.slice(i, i + chunkSize);
        // do whatever
        result.push({
          items: chunk,
          inProgress: false,
          completed: false,
          success: [],
          failure: [],
        });
      }
      return result;
    },
    setUpTask(r) {
      if (r) {
        this.chunkSize = r.chunkSize;
        this.csv = r.csv;
        this.task = null;
        this.emit = r.emit ? r.emit : null;
        this.task = {
          chunks: this.chunk(r.items),
          request: r.request,
          url: r.url,
          titleProp: r.titleProp,
          name: r.name,
          action: r.action,
          dataProp: r.dataProp,
          includeSecret: r.includeSecret,
          clearSelection: r.clearSelection,
        };
        this.dialog = true;
      } else {
        console.log("invalid request");
      }
    },
    init() {
      debugger;
      this.loading = true;
      this.$emit("notReady");
      this.actions.forEach((element) => {
        /*  var props = {
          availableIds: [],
          unavailableIds: [],
          assessments: element.data.ids,
        };
        */
        this.$post(this.url, element.data).then((response) => {
          debugger;
          if (response.error) {
            this.error = response.text;
          } else {
            element.props.availableIds = Object.keys(response.success);
            element.props.unavailableIds = Object.keys(response.failure).map((x) => {
              return {
                id: x,
                msg: response.failure[x],
              };
            });
            this.actionProps = this.actionProps.filter((x) => x.name != element.name);
            this.actionProps.push({
              name: element.name,
              props: [element.props],
            });
          }
          this.$emit("ready");
        });
      });
      this.loading = false;
    },
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    actions: {
      type: Array,
      default: () => [],
    },
    url: {
      type: String,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
};
</script>
